// CareLogix™ ORACLE — Google OAuth callback page (Phase 23).
//
// When users return from Emergent Auth, they land at `<origin>/#session_id=…`.
// `AuthCallback` extracts the fragment, calls our backend `/auth/google/session`
// once (idempotent), persists the session, then redirects to the ORACLE home.
//
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE EMERGENT GOOGLE AUTH FLOW.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

function extractSessionId(hash) {
  if (!hash) return null;
  const cleaned = hash.startsWith("#") ? hash.slice(1) : hash;
  const params = new URLSearchParams(cleaned);
  return params.get("session_id");
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const { exchangeGoogleSession } = useAuth();
  const hasProcessed = useRef(false);
  const [msg, setMsg] = useState("Completing Google sign-in…");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const sessionId = extractSessionId(window.location.hash);
    if (!sessionId) {
      setErr("Missing session identifier from the OAuth provider.");
      return;
    }

    (async () => {
      try {
        const payload = await exchangeGoogleSession(sessionId);
        const name = payload?.user?.name || payload?.user?.email || "back";
        setMsg(`Welcome ${name}. Redirecting to ORACLE…`);
        // Clean the fragment so a refresh does not retrigger the exchange
        try {
          const cleanUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
          window.history.replaceState({}, document.title, cleanUrl);
        } catch { /* ignore */ }
        setTimeout(() => navigate("/", { replace: true, state: { user: payload?.user } }), 400);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        setErr(typeof detail === "string" ? detail : (e?.message || "Sign-in failed."));
      }
    })();
  }, [exchangeGoogleSession, navigate]);

  return (
    <main className="cl-access cl-access--callback" data-testid="auth-callback-page">
      <header className="cl-access__hero">
        <div className="cl-access__logo" aria-hidden="true">
          <span className="cl-access__logo-glyph">🜂</span>
        </div>
        <h1 className="cl-access__title">CareLogix™ ORACLE</h1>
        <p className="cl-access__tagline">Global Market Prediction &amp; Intelligence Engine</p>
      </header>
      <section className="cl-access__callback-card">
        {!err && <p className="cl-access__info" data-testid="auth-callback-msg">{msg}</p>}
        {err && (
          <>
            <p className="cl-access__err" data-testid="auth-callback-err">{err}</p>
            <button
              type="button"
              className="cl-access__btn cl-access__btn--gold"
              onClick={() => navigate("/access", { replace: true })}
              data-testid="auth-callback-retry"
            >Back to sign-in</button>
          </>
        )}
      </section>
    </main>
  );
}
